import EventEmitter from "@/shared/utils/event-emitter";

export interface IAudioContextService {
  devices: MediaDeviceInfo[],
  selectedDevice: MediaDeviceInfo | null,
  stream: MediaStream | null,
  audioContext: AudioContext | null,
  volume: number,
  isMicMuted: boolean // 마이크 음소거 상태 추가
}

export default class AudioContextService extends EventEmitter {
  private static DEVICE_STORAGE_KEY = 'selected_audio_device';
  gainNode: GainNode | null = null;
  analyserNode: AnalyserNode | null = null;
  audioContext: AudioContext | null = null;
  volume: number = 0.29;
  stream: MediaStream | null = null;
  devices: MediaDeviceInfo[] = [];
  selectedDevice: MediaDeviceInfo | null = null;
  windowFocusHandler: EventListener;  // 추가: window focus 핸들러
  private isMicMuted: boolean = false; // 마이크 음소거 상태

  //1. 디바이스 목록 가져오기
  //2. 디폴트 디바이스 설정
  //3. 선택된 디바이스에 맞는 스트림 생성
  //4. 스트림을 이용한 오디오 컨텍스트 생성
  //5. 오디오 컨텍스트 재개
  //6. 볼륨 설정
  //7. 피치값 가져오기
  //8. 소멸

  constructor() {
    super();
    this.windowFocusHandler = this.resumeContext.bind(this);

    // 수정: window focus 이벤트 리스너에 이미 bind된 handler 사용
    window.addEventListener('focus', this.windowFocusHandler);
  }

  async init(): Promise<IAudioContextService> {
    await this.getDevices();
    await this.setDefaultDevice();
    await this.initStream(this.selectedDevice?.deviceId ?? "");
    await this.initAudioContext(this.stream);

    const state = {
      devices: this.devices,
      selectedDevice: this.selectedDevice,
      stream: this.stream,
      audioContext: this.audioContext,
      volume: this.volume,
      isMicMuted: this.isMicMuted
    }
    
    this.emit('state', [state]);
    return state
  }

  //장치를 새로고침 + 디폴트에 해당하는 장치를 찾아 매칭
  async getDevices() {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices()
      this.devices = allDevices.filter(device => device.kind === 'audioinput')

      return this.devices
    } catch (err) {
      console.error(err)
    }
  }

  setDefaultDevice() {
    if (this.devices.length === 0) return;

    const defaultLabeledDevice = this.devices.find(device => device.deviceId === "default") ?? null
    const defaultDevice = this.devices
      .filter((device) => device.deviceId !== "default")
      .find(device => defaultLabeledDevice?.label.includes(device?.label ?? ""))

    this.selectedDevice = (defaultDevice ? defaultDevice : defaultLabeledDevice)

    return this.selectedDevice
  }

  //장치에 해당하는 미디어 스트림 생성
  async initStream(deviceId: string) {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })

      this.stream = stream

      return this.stream
    } catch (err) {
      console.error(err)
    }
  }

  async initAudioContext(stream: MediaStream | null) {
    if (!stream) return;

    this.audioContext = new AudioContext();
    this.gainNode = this.audioContext.createGain();
    this.analyserNode = this.audioContext.createAnalyser();
    const source = this.audioContext.createMediaStreamSource(stream);
    source.connect(this.gainNode);
    this.gainNode.connect(this.analyserNode);
    this.gainNode.gain.value = this.isMicMuted ? 0 : this.volume;
  }

  async resumeContext() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      
      await this.audioContext.resume();
    }
  }

  setVolume(value: number) {
    this.volume = Math.max(0, Math.min(2, value));
    if (this.gainNode) this.gainNode.gain.value = this.volume;
  }

  getPitch(): number {
    if (!this.analyserNode) return 0;

    const bufferLength = this.analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyserNode.getByteFrequencyData(dataArray);

    // 최대값 계산 (0~1 사이 정규화)
    const max = Array.from(dataArray).reduce((acc, val) => Math.max(acc, val / 255), 0);
    return Math.min(100, Math.round(max * 100));
  }

  // 마이크 음소거/해제 메서드 추가
  muteMic() {
    this.isMicMuted = true;
    if (this.gainNode) {
      this.gainNode.gain.value = 0;
    }
    
  }

  unmuteMic() {
    this.isMicMuted = false;
    if (this.gainNode) {
      this.gainNode.gain.value = this.volume;
    }
    
  }

  // 현재 마이크 음소거 상태 반환
  getMicMuteState(): boolean {
    return this.isMicMuted;
  }

  async stopStream() {
    if (this.stream) {
      await this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }

  destroy() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }

    if (this.audioContext) {
      this.audioContext.close();
    }
    // 추가: window focus 이벤트 핸들러 해제
    window.removeEventListener('focus', this.windowFocusHandler);

    this.removeAllListeners();

    this.gainNode = null;
    this.analyserNode = null;
    this.audioContext = null;
    this.stream = null;
    this.devices = [];
    this.selectedDevice = null;
    this.isMicMuted = false;
  }

}